import { observer } from 'mobx-react-lite';
import React from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { useGlobal } from '~/hooks/useGlobal';
import { useStores } from '~/hooks/useStore';

import { DESKTOP_HEADER_ID } from '../Header/constants';
import { HeaderDubaiExpressNavigation } from './HeaderDubaiExpressNavigation';
import { HeaderDubaiExpressZones } from './HeaderDubaiExpressZones';
import { useActiveZone } from './hooks/useActiveZone';
import { useScrolled } from './hooks/useScrolled';

/** Top-level categories shown as zones; the shop tree can hold more. */
const MAX_ZONES = 5;

type HeaderDubaiExpressProps = Omit<
  React.ComponentPropsWithRef<'header'>,
  'children' | 'id'
>;

/**
 * Header of the DubaiExpress marketplace, built to the marketplace mockup.
 *
 * Desktop: logo, zone buttons and the user actions (search, favorites,
 * profile, cart) in one row. Mobile: a centered logo over a row of zone
 * buttons; the actions live in the bottom navigation. The header is white
 * and gets a hairline once scrolled; inside a zone it takes the zone's CMS
 * background color and rounded bottom corners. On mobile its background runs
 * on above its top edge: in-app browsers such as Telegram's on iOS stick it
 * below their translucent address bar, and the strip under that bar would
 * otherwise show the page scrolling past.
 */
const HeaderDubaiExpress = React.forwardRef<
  HTMLElement,
  HeaderDubaiExpressProps
>(({ className, style, ...props }, ref) => {
  const { isMobile } = useGlobal();
  const { shopCategories } = useStores();
  const scrolled = useScrolled(isMobile);

  const zones = shopCategories.list.slice(0, MAX_ZONES);
  const { zone: activeZone, isLanding } = useActiveZone(zones);
  const zoneBackground = activeZone?.backgroundColor || undefined;
  const isRounded = scrolled || Boolean(zoneBackground && !isLanding);

  const logo = (
    <Link to="/" className={cn('flex', !isMobile && 'justify-self-start')}>
      <img
        src={company.images.logo.dark}
        alt={company.config.name}
        className={cn(
          'w-auto max-w-[200px] object-contain',
          isMobile ? 'h-7' : 'h-8',
        )}
      />
    </Link>
  );

  return (
    <header
      {...props}
      ref={ref}
      id={DESKTOP_HEADER_ID}
      data-company={company.name}
      style={{ ...style, backgroundColor: zoneBackground ?? '#fff' }}
      className={cn(
        'z-12 w-full text-[#141415] antialiased transition-[background-color,border-radius,box-shadow] duration-200 ease-[cubic-bezier(.22,1,.36,1)] [font-family:var(--fontFamily2)] [text-rendering:auto]',
        'desktop:fixed sticky desktop:inset-x-0 top-0',
        isMobile &&
          'before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-[100vh] before:bg-inherit',
        isRounded && 'desktop:rounded-b-[24px] rounded-b-[16px]',
        scrolled && !zoneBackground && 'shadow-[0_1px_0_#EDEDED]',
        className,
      )}
    >
      {isMobile ? (
        <>
          <div className="flex justify-center px-3.5 pt-safe-offset-3">
            {logo}
          </div>
          <HeaderDubaiExpressZones zones={zones} activeId={activeZone?.id} />
        </>
      ) : (
        <div className="mx-auto grid max-w-[1440px] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-6 px-10 py-[18px]">
          {logo}
          <HeaderDubaiExpressZones zones={zones} activeId={activeZone?.id} />
          <div className="flex shrink-0 items-center gap-5 justify-self-end">
            <HeaderDubaiExpressNavigation />
          </div>
        </div>
      )}
    </header>
  );
});
HeaderDubaiExpress.displayName = 'HeaderDubaiExpress';

export default observer(HeaderDubaiExpress);
